import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { TMSCommodity } from 'types/Load';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

type GetThreeGCommoditiesResponse = {
  commodityList: TMSCommodity[];
  tmsTenant?: string;
  message?: string;
};

/**
 * Fetches customer-scoped commodities for ThreeG item editors.
 */
export async function getThreeGCommodities(
  tmsID: number,
  options: {
    // Numeric customer ID preferred; externalTmsId can be used as fallback.
    customerID?: number;
    externalTmsId?: string;
    forceRefresh?: boolean;
    freightClass?: string;
  }
): Promise<Result<GetThreeGCommoditiesResponse, ApiError>> {
  if (!options.customerID && !options.externalTmsId) {
    return ok({ commodityList: [], tmsTenant: '' });
  }

  const queryParams = new URLSearchParams({
    tmsID: tmsID.toString(),
  });

  if (options.customerID) {
    queryParams.append('customerID', options.customerID.toString());
  }

  if (options.externalTmsId) {
    queryParams.append('externalTmsId', options.externalTmsId);
  }

  if (options.forceRefresh) {
    queryParams.append('forceRefresh', 'true');
  }

  if (options.freightClass?.trim()) {
    queryParams.append('freightClass', options.freightClass.trim());
  }

  const query = queryParams.toString();

  try {
    const response = await axios.get<GetThreeGCommoditiesResponse>(
      `/commodities?${query}`
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, {
      functionName: 'getThreeGCommodities',
      path: '/commodities',
    });
    if (isAxiosError(error)) {
      return err({
        message:
          error.response?.data?.message || 'Failed to get ThreeG commodities',
      });
    }
    return err({ message: 'Failed to get ThreeG commodities' });
  }
}
